import type { RiskWeights } from '../api'

/**
 * Redistributes risk weights when one slider moves, keeping the sum at
 * exactly 100 with all integer values.
 *
 * The proportional redistribution step produces floating-point residue
 * (e.g. 50 - (5 * 50) / 95 = 47.368...). We round every weight, then
 * absorb the ±N residual into the largest un-adjusted weights so the
 * total is always exactly 100.
 *
 * @param weights   Current weight state (sum expected to be 100)
 * @param key       The slider the user moved
 * @param newValue  The new value for that slider (clamped to [0, 100])
 */
export function redistributeWeights(
  weights: RiskWeights,
  key: keyof RiskWeights,
  newValue: number,
): RiskWeights {
  const clamped = Math.max(0, Math.min(100, newValue))
  const next = { ...weights } as Record<keyof RiskWeights, number>
  const diff = clamped - next[key]
  next[key] = clamped

  const otherKeys = (Object.keys(next) as Array<keyof RiskWeights>).filter((k) => k !== key)
  const otherSum = otherKeys.reduce((sum, k) => sum + next[k], 0)

  if (otherSum > 0) {
    otherKeys.forEach((k) => {
      next[k] = Math.max(0, next[k] - (diff * next[k]) / otherSum)
    })
  }

  const rounded = {} as Record<keyof RiskWeights, number>
  ;(Object.keys(next) as Array<keyof RiskWeights>).forEach((k) => {
    rounded[k] = Math.round(next[k])
  })

  const sum = (Object.keys(rounded) as Array<keyof RiskWeights>).reduce(
    (s, k) => s + rounded[k],
    0,
  )
  let residual = 100 - sum

  if (residual !== 0 && otherKeys.length > 0) {
    const sortedOthers = [...otherKeys].sort((a, b) => rounded[b] - rounded[a])
    const step = residual > 0 ? 1 : -1
    let cursor = 0
    let safety = 0
    // Generous cap: normal residual is ±2 from rounding, but the
    // all-others-zero fallback can push residual up to ~100 when the
    // proportional branch is skipped (otherSum===0). 200x handles that.
    while (residual !== 0 && safety < sortedOthers.length * 200) {
      const k = sortedOthers[cursor % sortedOthers.length]
      const nextVal = rounded[k] + step
      if (nextVal >= 0 && nextVal <= 100) {
        rounded[k] = nextVal
        residual -= step
      }
      cursor++
      safety++
    }
  }

  return rounded as RiskWeights
}
